import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { BillData, BillSubmissionState } from '@shared/schema/billData';
import type { UserProfile } from '@shared/schema/userProfile';

/**
 * All app data lives in IndexedDB, on-device only — no accounts, no
 * server-side storage (see docs/privacy.md). `idb` is a thin promise
 * wrapper around the native IndexedDB API; nothing heavier (like Dexie) is
 * warranted for two small object stores.
 */
export interface BillRecord {
  id: string;
  createdAt: number;
  updatedAt: number;
  billData: BillData;
  submissionState: BillSubmissionState;
  submittedAt?: number;
  /** Small downscaled copy for the history list thumbnail. */
  imageThumbnail?: Blob;
  /** Full-resolution copy, kept separately for the actual IRD photo share/upload step. */
  imageFull?: Blob;
}

interface IrdAppDB extends DBSchema {
  bills: {
    key: string;
    value: BillRecord;
    indexes: { 'by-createdAt': number; 'by-sellerPan': string };
  };
  profile: {
    key: string;
    value: UserProfile;
  };
}

const DB_NAME = 'ird-bill-scanner';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<IrdAppDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<IrdAppDB>> {
  dbPromise ??= openDB<IrdAppDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const bills = db.createObjectStore('bills', { keyPath: 'id' });
      bills.createIndex('by-createdAt', 'createdAt');
      bills.createIndex('by-sellerPan', 'billData.sellerPan.value');
      db.createObjectStore('profile', { keyPath: 'id' });
    },
  });
  return dbPromise;
}

/**
 * Test-only: closes and drops the cached connection. Must actually `close()`
 * the underlying IDBDatabase, not just discard the reference — IndexedDB's
 * `deleteDatabase` blocks indefinitely while any connection to that database
 * remains open, which otherwise hangs a test's `deleteDB()` cleanup forever.
 */
export async function _resetDbConnectionForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
  }
  dbPromise = null;
}
